import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Link, useSearchParams } from 'react-router';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { ServiceGraph } from '../components/ServiceGraph';
import { compareBy, useUrlFlag, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import { ClassificationBadge } from '../components/Classification';
import { ProductLineCombo } from '../components/ProductLineCombo';
import { canClassifyRepo } from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import { formatDate, formatDateTime } from '../lib/format';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';

// Below this product-line confidence, an auto classification is "needs review".
const REVIEW_THRESHOLD = 70;

const INVENTORY = gql`
  query Inventory {
    repos {
      id
      gitlabProjectId
      pathWithNamespace
      groupPath
      visibility
      archived
      lastActivityAt
      confidence
      deploymentCount
      classification
      productLine
      productLineConfidence
      productLineSource
      owners {
        role
        displayName
        gitlabUsername
        email
      }
    }
    owners {
      id
    }
    productLines
    # The GITLAB run specifically — this table is what that scanner produces.
    # Unfiltered, this returned whichever scanner finished last and put e.g.
    # "aws_secrets · succeeded" above the repos, which reads as a verdict on the
    # rows underneath and is not one.
    scanRuns(limit: 1, scanner: "gitlab") {
      id
      scanner
      status
      startedAt
    }
  }
`;

const SET_PRODUCT_LINE = gql`
  mutation SetRepoProductLine($gitlabProjectId: Float!, $productLine: String!) {
    setRepoProductLine(gitlabProjectId: $gitlabProjectId, productLine: $productLine) {
      id
      productLine
      productLineConfidence
      productLineSource
      classification
    }
  }
`;

const DIRECTORY = gql`
  query AssignableUsers {
    assignableUsers { email displayName }
  }
`;

const SET_OWNERS_BULK = gql`
  mutation SetOwnersBulk($subjectIds: [String!]!, $role: String!, $email: String!, $filterNote: String) {
    setOwnersBulk(subjectType: "repo", subjectIds: $subjectIds, role: $role, email: $email, filterNote: $filterNote) {
      role
      email
      assignedCount
      skipped { subjectId reason }
    }
  }
`;

interface Repo {
  id: string;
  gitlabProjectId: number;
  pathWithNamespace: string;
  groupPath?: string;
  visibility?: string;
  archived?: boolean;
  lastActivityAt?: string;
  confidence: string;
  deploymentCount: number;
  classification: string;
  productLine?: string;
  productLineConfidence?: number;
  productLineSource?: string;
  owners: RepoOwnerLite[];
}
interface RepoOwnerLite {
  role: string;
  displayName?: string;
  gitlabUsername?: string;
  email?: string;
}
/** EVERY holder of a role, not just the first — maintainer and engineer are rosters. */
const ownerNames = (r: Repo, role: string): string[] =>
  r.owners.filter((x) => x.role === role).map((o) => o.displayName || o.gitlabUsername || o.email || '—');
const hasOwner = (r: Repo) => r.owners.some((o) => o.role === 'owner');
interface Data {
  repos: Repo[];
  owners: { id: string }[];
  productLines: string[];
  scanRuns: { scanner: string; status: string; startedAt: string }[];
}

const isNeedsReview = (r: Repo) =>
  (r.productLineSource !== 'human' && (r.productLineConfidence ?? 0) < REVIEW_THRESHOLD) ||
  !r.productLine ||
  r.productLine === 'unclassified';

// Comma-joined URL param ⇄ Set. Empty string = the facet is off (matches everything).
const parseMulti = (raw: string): Set<string> => new Set(raw ? raw.split(',').filter(Boolean) : []);
const joinMulti = (s: Set<string>): string => [...s].sort().join(',');

/**
 * A checkbox-dropdown facet with per-value counts (Datadog/Carbon multi-select model).
 * OR within the facet; empty selection = no constraint. Counts come from the caller
 * (computed against the OTHER active facets, so they reflect what a click would yield).
 */
function MultiFacet({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; count: number }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const n = selected.size;
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };
  return (
    <div className="col-picker" style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} className={n ? 'active' : ''} title={`Filter by ${label.toLowerCase()}`}>
        {label}
        {n > 0 && <span className="tag-badge" style={{ marginLeft: 6 }}>{n}</span>}
        <span aria-hidden> ▾</span>
      </button>
      {open && (
        <div className="col-picker-panel" style={{ left: 0, minWidth: 200, maxHeight: 320, overflow: 'auto' }}>
          {options.length === 0 && <div className="cell-meta" style={{ padding: '6px 8px' }}>no values</div>}
          {options.map((o) => (
            <label key={o.value} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ display: 'flex', gap: 6 }}>
                <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggle(o.value)} />
                {o.value || <em className="cell-meta">(none)</em>}
              </span>
              <span className="cell-meta">{o.count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Confidence/source badge — surfaces the review state right next to the value:
 *  manual (human override, frozen) vs an auto confidence % (amber below threshold). */
function ReviewBadge({ r }: { r: Repo }) {
  if (r.productLineSource === 'human') {
    return <span className="badge confirmed" title="manually classified — frozen against rescans">manual</span>;
  }
  const conf = r.productLineConfidence;
  return (
    <span className={`badge ${(conf ?? 0) < REVIEW_THRESHOLD ? 'inferred' : ''}`} title={`auto (${r.productLineSource ?? 'rule'})`}>
      {conf != null ? `${conf}%` : '—'}
    </span>
  );
}

/** Read-only product-line cell (non-editors) — value + review badge. */
function ProductLineReadOnly({ r }: { r: Repo }) {
  return (
    <span className="pl-cell">
      {r.productLine && r.productLine !== 'unclassified' ? (
        <span className="tag-badge">{r.productLine}</span>
      ) : (
        <span className="cell-meta">—</span>
      )}
      <ReviewBadge r={r} />
    </span>
  );
}

/** Read-only owner/maintainer/engineer cell (assignment happens on the repo detail
 *  page). Renders every holder — a roster showing only its first member reads as
 *  "one maintainer" and is how you miss that a repo has four. */
function OwnerCell({ names }: { names: string[] }) {
  if (!names.length) return <span className="cell-meta">—</span>;
  return (
    <>
      {names.map((n) => (
        <span className="tag-badge" key={n} style={{ marginRight: 4 }}>
          {n}
        </span>
      ))}
    </>
  );
}

// Column registry. `basic` = shown by default; the rest are opt-in via the
// Columns picker. Order here = column order. `name` is locked (can't hide).
interface Col {
  key: string;
  label: string;
  basic: boolean;
  num?: boolean;
  render: (r: Repo) => React.ReactNode;
}
const COLUMNS: Col[] = [
  {
    key: 'name',
    label: 'Repo',
    basic: true,
    render: (r) => (
      <div className="repo-cell">
        <div className="repo-cell-title">
          <Link className="table-title" to={`/repos/${r.gitlabProjectId}`}>
            {r.pathWithNamespace.split('/').pop()}
          </Link>
          {r.archived && <span className="badge">archived</span>}
        </div>
        <div className="repo-cell-path muted">{r.pathWithNamespace}</div>
      </div>
    ),
  },
  {
    key: 'productLine',
    label: 'Product line',
    basic: true,
    render: (r) => <ProductLineReadOnly r={r} />,
  },
  {
    key: 'classification',
    label: 'Type',
    basic: true,
    render: (r) => <ClassificationBadge value={r.classification} />,
  },
  {
    key: 'owner',
    label: 'Owner',
    basic: true,
    render: (r) => <OwnerCell names={ownerNames(r, 'owner')} />,
  },
  {
    key: 'maintainer',
    label: 'Maintainer',
    basic: false,
    render: (r) => <OwnerCell names={ownerNames(r, 'maintainer')} />,
  },
  {
    key: 'groupPath',
    label: 'Group',
    basic: true,
    render: (r) => <span className="cell-meta">{r.groupPath ?? '—'}</span>,
  },
  {
    key: 'visibility',
    label: 'Visibility',
    basic: true,
    render: (r) => <span className="cell-meta">{r.visibility ?? '—'}</span>,
  },
  {
    key: 'lastActivityAt',
    label: 'Last activity',
    basic: true,
    render: (r) => <span className="cell-meta">{formatDate(r.lastActivityAt)}</span>,
  },
  {
    key: 'archived',
    label: 'Archived',
    basic: false,
    render: (r) =>
      r.archived ? <span className="badge">archived</span> : <span className="cell-meta">—</span>,
  },
  {
    key: 'deploymentCount',
    label: 'Deployments',
    basic: false,
    num: true,
    render: (r) => <span className="cell-meta">{r.deploymentCount}</span>,
  },
  {
    key: 'confidence',
    label: 'Confidence',
    basic: false,
    render: (r) => (
      <span className={`badge ${r.confidence === 'confirmed' ? 'confirmed' : 'inferred'}`}>
        {r.confidence}
      </span>
    ),
  },
  {
    key: 'gitlabProjectId',
    label: 'GitLab ID',
    basic: false,
    num: true,
    render: (r) => <span className="cell-meta">{r.gitlabProjectId}</span>,
  },
];

/**
 * What each sortable column sorts BY. Separate from COLUMNS.render because the
 * rendered cell is JSX (and sometimes a picker), while sorting needs the scalar
 * behind it — `owner` renders a chip but sorts on the owner's email.
 * A column absent here simply isn't sortable.
 */
const SORT_PICK: Record<string, (r: Repo) => unknown> = {
  name: (r) => r.pathWithNamespace,
  productLine: (r) => r.productLine,
  classification: (r) => r.classification,
  owner: (r) => r.owners.find((o) => o.role === 'owner')?.email,
  maintainer: (r) => r.owners.find((o) => o.role === 'maintainer')?.email,
  groupPath: (r) => r.groupPath,
  visibility: (r) => r.visibility,
  lastActivityAt: (r) => r.lastActivityAt,
  archived: (r) => Boolean(r.archived),
  deploymentCount: (r) => r.deploymentCount,
  confidence: (r) => r.confidence,
  gitlabProjectId: (r) => r.gitlabProjectId,
};

// v3: reset stale prefs so the new Owner column shows by default.
const LS_KEY = 'infras.repoColumns.v3';
function loadColumns(): Set<string> {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (Array.isArray(saved) && saved.length) {
      const next = new Set(saved as string[]);
      next.add('name');
      return next;
    }
  } catch {
    /* fall through to defaults */
  }
  return new Set(COLUMNS.filter((c) => c.basic).map((c) => c.key));
}

export function InventoryPage() {
  const { data, loading, error, refetch } = useQuery<Data>(INVENTORY, { fetchPolicy: 'cache-and-network' });
  const dir = useQuery<{ assignableUsers: { email: string; displayName?: string }[] }>(DIRECTORY);
  const { role } = useMe();
  const canClassify = !!role && canClassifyRepo(role); // product-line curation = root|maintainer
  const canAssign = role === 'root' || role === 'maintainer'; // owner/maintainer assignment
  const [setProductLine] = useMutation(SET_PRODUCT_LINE);
  const [setOwnersBulk, bulkState] = useMutation(SET_OWNERS_BULK);
  // Filters + sort + page live in the URL so a narrowed table is a shareable link.
  // Column visibility stays in localStorage: that's a per-person display habit,
  // not something you'd paste to a colleague.
  const [, setParams] = useSearchParams(); // for the atomic multi-key clearFilters
  const [query, setQuery] = useUrlState('q');
  const [plRaw, setPlRaw] = useUrlState('pl'); // product line — comma-joined multi
  const [typeRaw, setTypeRaw] = useUrlState('type'); // kind — comma-joined multi
  const [ownerFilter, setOwnerFilter] = useUrlState('owner'); // '' = any · 'none' = unassigned
  const [archived, setArchived] = useUrlState('archived', 'active'); // active | archived | all
  const [needsReviewOnly, setNeedsReviewOnly] = useUrlFlag('review');
  const [view, setView] = useUrlState('view', 'table');
  const sort = useUrlSort('name');
  const [page, setPage] = useUrlNumber('page', 1);
  const [visible, setVisible] = useState<Set<string>>(loadColumns);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Bulk selection: a page-scoped id set, OR "all matching the current filter".
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkRole, setBulkRole] = useState<'owner' | 'maintainer' | null>(null); // opens the person picker
  const [confirm, setConfirm] = useState<{ role: 'owner' | 'maintainer'; email: string } | null>(null);
  const [bulkResult, setBulkResult] = useState<{ assignedCount: number; skipped: { subjectId: string; reason: string }[] } | null>(null);

  const repos = data?.repos ?? [];
  const productLines = data?.productLines ?? [];
  const users = dir.data?.assignableUsers ?? [];
  const plSel = parseMulti(plRaw);
  const typeSel = parseMulti(typeRaw);
  const q = query.trim().toLowerCase();

  // Individual predicates so facet counts can be computed against the OTHER facets.
  const mArch = (r: Repo) => (archived === 'active' ? !r.archived : archived === 'archived' ? !!r.archived : true);
  const mSearch = (r: Repo) => !q || r.pathWithNamespace.toLowerCase().includes(q);
  const mPl = (r: Repo) => plSel.size === 0 || plSel.has(r.productLine || 'unclassified');
  const mType = (r: Repo) => typeSel.size === 0 || typeSel.has(r.classification);
  const mOwner = (r: Repo) => ownerFilter !== 'none' || !hasOwner(r);
  const mReview = (r: Repo) => !needsReviewOnly || isNeedsReview(r);

  const filtered = repos.filter((r) => mArch(r) && mSearch(r) && mPl(r) && mType(r) && mOwner(r) && mReview(r));

  // Dynamic facet counts: each value counted over the rows passing every OTHER facet.
  const countBy = (getKey: (r: Repo) => string, exclude: (r: Repo) => boolean) => {
    const m = new Map<string, number>();
    for (const r of repos) {
      if (!(mArch(r) && mSearch(r) && mOwner(r) && mReview(r))) continue;
      if (!exclude(r)) continue;
      const k = getKey(r);
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  };
  const plCounts = countBy((r) => r.productLine || 'unclassified', (r) => mType(r));
  const plOptions = [...new Set([...repos.map((r) => r.productLine || 'unclassified'), ...productLines])]
    .sort()
    .map((v) => ({ value: v, count: plCounts.get(v) ?? 0 }));
  const typeCounts = countBy((r) => r.classification, (r) => mPl(r));
  const typeOptions = [...new Set(repos.map((r) => r.classification).filter(Boolean))]
    .sort()
    .map((v) => ({ value: v, count: typeCounts.get(v) ?? 0 }));

  // Counts exclude archived — they drive the daily-curation backlog, not the noise.
  const active = repos.filter((r) => !r.archived);
  const archivedCount = repos.length - active.length;
  const reviewCount = active.filter(isNeedsReview).length;
  const needsOwnerCount = active.filter((r) => !hasOwner(r)).length;

  // Archived always sorts to the bottom (secondary key), then the chosen column.
  const sortedBase = compareBy(filtered, (r) => SORT_PICK[sort.key]?.(r), sort.dir);
  const sorted = [...sortedBase].sort((a, b) => Number(!!a.archived) - Number(!!b.archived));
  const pg = usePaged(sorted, 25, { page, onPage: setPage });
  const cols = COLUMNS.filter((c) => visible.has(c.key));
  const anyFilter = Boolean(q || plSel.size || typeSel.size || ownerFilter || needsReviewOnly || archived !== 'active');
  // Clear every filter in ONE setParams: calling six useUrlState setters in a row
  // does NOT compose — each rebuilds from the same stale `prev` and drops only its
  // own key, so the last write (archived) restores the others (the same pitfall
  // useUrlSort documents). Removing all filter keys at once is the only correct way;
  // view/sort/dir are deliberately preserved. Also drops the now-stale selection.
  const clearFilters = () => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ['q', 'pl', 'type', 'owner', 'review', 'archived', 'page'].forEach((k) => next.delete(k));
        return next;
      },
      { replace: true },
    );
    clearSelection();
  };

  // A one-line description of the active filter — recorded on the bulk audit rows
  // and shown in the confirm modal, so the scope of a bulk assign is explicit.
  const filterNote =
    [
      plSel.size ? `product line: ${[...plSel].join(', ')}` : '',
      typeSel.size ? `type: ${[...typeSel].join(', ')}` : '',
      ownerFilter === 'none' ? 'unassigned' : '',
      needsReviewOnly ? 'needs review' : '',
      archived !== 'active' ? `archived: ${archived}` : '',
      q ? `search: "${q}"` : '',
    ]
      .filter(Boolean)
      .join(' · ') || 'all repos';

  const classify = (gitlabProjectId: number, productLine: string) =>
    void setProductLine({ variables: { gitlabProjectId, productLine } });

  // ── selection helpers ──────────────────────────────────────────────────────
  const pageIds = pg.pageItems.map((r) => r.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const selectionCount = allMatching ? filtered.length : selected.size;
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };
  const toggleRow = (id: string) => {
    setAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    setAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  // Any filter/nav change drops a stale selection (the scope it referred to moved).
  const resetSelectionThen = (fn: () => void) => { clearSelection(); fn(); };

  const runBulk = async () => {
    if (!confirm) return;
    const ids = allMatching ? filtered.map((r) => r.id) : [...selected];
    const res = await setOwnersBulk({
      variables: { subjectIds: ids, role: confirm.role, email: confirm.email, filterNote },
      refetchQueries: [INVENTORY],
    });
    const out = (res.data as any)?.setOwnersBulk;
    setBulkResult({ assignedCount: out?.assignedCount ?? 0, skipped: out?.skipped ?? [] });
    setConfirm(null);
    setBulkRole(null);
    clearSelection();
  };

  const toggleCol = (key: string) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // name is mandatory (it's the link to detail)
      next.add('name');
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  if (loading && !data)
    return (
      <div className="page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  // The old copy named VITE_GRAPHQL_URL, which the reader can do nothing with —
  // and it read as "you broke your setup" to a new member on their first day.
  // Say what happened, what to do, and that nothing is lost; keep the technical
  // detail for whoever is on call.
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load the inventory — the API didn&apos;t respond.</p>
        <p className="muted">
          Scans keep running in the background, so nothing is lost. If this persists, ask in
          #devops-infra.
        </p>
        <details style={{ marginTop: 12 }}>
          <summary className="muted">Details</summary>
          <p className="error">{error.message}</p>
        </details>
      </div>
    );

  const lastRun = data?.scanRuns?.[0];
  const groups = new Set(repos.map((r) => r.groupPath).filter(Boolean));
  const deployed = repos.filter((r) => r.deploymentCount > 0).length;
  // Counted over ACTIVE repos only — archived ones are not the daily backlog, so
  // counting them would inflate "needs owner" with noise nobody works.
  const unowned = needsOwnerCount;

  return (
    <div className="page">
      <div className="stat-row">
        <Stat label="Repos" value={repos.length} />
        <Stat label="Groups" value={groups.size} />
        <Stat label="Deployed (k8s)" value={deployed} />
        <Stat label="Needs review" value={reviewCount} />
        <div className="stat">
          {/* Names what it measures. "Last scan" invited the reader to assume it
              was about this table; it is now actually about this table. */}
          <div className="label">Repos last scanned</div>
          <div className="stat-status">
            {lastRun ? (
              <span
                className={`badge ${
                  lastRun.status === 'succeeded'
                    ? 'confirmed'
                    : lastRun.status === 'failed'
                      ? 'failed'
                      : ''
                }`}
              >
                {lastRun.status}
              </span>
            ) : (
              <span className="muted">never</span>
            )}
          </div>
          {lastRun?.startedAt && (
            <div className="muted text-2xs" style={{ marginTop: 4 }}>
              {formatDateTime(lastRun.startedAt)}
            </div>
          )}
        </div>
      </div>

      {/* Phase 6 §3: an absent owner IS the finding, and a count is what makes
          anyone act on it — an empty column in a table gets scrolled past. This
          used to live on the Overview, which is currently unrouted, so the
          number sat where nobody could see it. Here it is one click from the
          filter that acts on it. */}
      {unowned > 0 && ownerFilter !== 'none' && (
        <p className="unowned-note">
          <strong>{unowned}</strong> of {active.length} active repos have no owner
          {archivedCount > 0 && <span className="cell-meta"> (+{archivedCount} archived hidden)</span>}.{' '}
          <button className="mini-btn" onClick={() => resetSelectionThen(() => setOwnerFilter('none'))}>
            Show them
          </button>{' '}
          — an owner is the person a question or an incident goes to.
        </p>
      )}

      {/* Table and graph are two VIEWS of the same route, not two pages — the
          segmented control writes ?view= so either one is a shareable link. */}
      <div className="toolbar">
        <div className="view-toggle">
          <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>
            Table
          </button>
          <button className={view === 'graph' ? 'active' : ''} onClick={() => setView('graph')}>
            Graph
          </button>
        </div>
      </div>

      {view === 'graph' ? (
        <ServiceGraph />
      ) : repos.length === 0 ? (
        <p className="muted">
          No repos yet — they appear once the <code>gitlab-scan</code> job has run.
        </p>
      ) : (
        <div className="list-panel">
          <div className="filter-row">
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
            {/* `type="search"` for the native clear affordance — the alternative is
                hand-rolling an × button that has to be positioned over the input. */}
            <input
              type="search"
              value={query}
              onChange={(e) => resetSelectionThen(() => setQuery(e.target.value))}
              placeholder="Search name or path…"
              aria-label="Search repos by name or group path"
              title="Substring match on the full path, e.g. weather · smallapp/weather · smallapp-frontend"
            />
            <MultiFacet
              label="Product line"
              options={plOptions}
              selected={plSel}
              onChange={(next) => resetSelectionThen(() => setPlRaw(joinMulti(next)))}
            />
            <MultiFacet
              label="Type"
              options={typeOptions}
              selected={typeSel}
              onChange={(next) => resetSelectionThen(() => setTypeRaw(joinMulti(next)))}
            />
            <select
              className="class-select"
              value={ownerFilter}
              onChange={(e) => resetSelectionThen(() => setOwnerFilter(e.target.value))}
              title="filter by owner status"
            >
              <option value="">Any owner</option>
              <option value="none">Unassigned ({needsOwnerCount})</option>
            </select>
            {/* Archived: active by default (the daily surface), still reachable. */}
            <div className="view-toggle" title="archived repos are hidden from daily curation by default">
              {(['active', 'archived', 'all'] as const).map((v) => (
                <button key={v} className={archived === v ? 'active' : ''} onClick={() => resetSelectionThen(() => setArchived(v))}>
                  {v === 'active' ? 'Active' : v === 'archived' ? `Archived${archivedCount ? ` (${archivedCount})` : ''}` : 'All'}
                </button>
              ))}
            </div>
            {canClassify && (
              <label className="row" style={{ gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={needsReviewOnly}
                  onChange={(e) => resetSelectionThen(() => setNeedsReviewOnly(e.target.checked))}
                />
                Needs classification ({reviewCount})
              </label>
            )}
            <span className="filter-count">
              {filtered.length} of {repos.length}
              {anyFilter && (
                <>
                  {' '}·{' '}
                  <button className="mini-btn" onClick={clearFilters}>Clear</button>
                </>
              )}
            </span>
            <div className="col-picker" style={{ marginLeft: 'auto' }}>
              <button onClick={() => setPickerOpen((o) => !o)}><Icon name="settings" /> Columns ({cols.length})</button>
              {pickerOpen && (
                <div className="col-picker-panel" style={{ left: 'auto', right: 0 }}>
                  {COLUMNS.map((col) => (
                    <label key={col.key} className={col.key === 'name' ? 'disabled' : ''}>
                      <input
                        type="checkbox"
                        checked={visible.has(col.key)}
                        disabled={col.key === 'name'}
                        onChange={() => toggleCol(col.key)}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bulk-action bar — appears when rows are selected (root|maintainer only). */}
          {canAssign && selectionCount > 0 && (
            <div className="bulk-bar" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', margin: '0 0 8px', background: 'var(--accent-soft, rgba(110,168,254,.12))', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
              <strong>{selectionCount} repo{selectionCount === 1 ? '' : 's'} selected</strong>
              <button className="mini-btn" onClick={() => setBulkRole('owner')}>Assign Owner ▾</button>
              <button className="mini-btn" onClick={() => setBulkRole('maintainer')}>Assign Maintainer ▾</button>
              {bulkRole && (
                <select
                  className="class-select"
                  autoFocus
                  value=""
                  onChange={(e) => e.target.value && setConfirm({ role: bulkRole, email: e.target.value })}
                >
                  <option value="">Pick a {bulkRole}…</option>
                  {users.map((u) => (
                    <option key={u.email} value={u.email}>{u.displayName ? `${u.displayName} · ${u.email}` : u.email}</option>
                  ))}
                </select>
              )}
              <button className="mini-btn" style={{ marginLeft: 'auto' }} onClick={clearSelection}>Cancel</button>
            </div>
          )}
          {canClassify && (
            <p className="muted text-2xs" style={{ margin: '0 0 8px' }}>
              Product line auto-classifies from the repo path. Pick a value to set it manually — a manual
              choice is <strong>frozen</strong> and every subsequent scan respects it. Use “Needs review
              only” to work the low-confidence backlog.
            </p>
          )}
          <div className="data-list-wrap">
            <table className="data-list">
              <thead>
                {/* Select-all-matching banner: when the whole page is picked and more
                    rows match the filter, offer to select the entire filtered set. */}
                {canAssign && pageAllSelected && !allMatching && filtered.length > pageIds.length && (
                  <tr>
                    <td colSpan={cols.length + 1} style={{ background: 'var(--accent-soft, rgba(110,168,254,.12))', padding: '6px 10px' }}>
                      All {pageIds.length} on this page selected ·{' '}
                      <button className="mini-btn" onClick={() => setAllMatching(true)}>
                        Select all {filtered.length} matching this filter
                      </button>
                    </td>
                  </tr>
                )}
                {canAssign && allMatching && (
                  <tr>
                    <td colSpan={cols.length + 1} style={{ background: 'var(--accent-soft, rgba(110,168,254,.12))', padding: '6px 10px' }}>
                      All <strong>{filtered.length}</strong> repos matching this filter are selected ·{' '}
                      <button className="mini-btn" onClick={clearSelection}>Clear selection</button>
                    </td>
                  </tr>
                )}
                <tr>
                  {canAssign && (
                    <th style={{ width: 28 }}>
                      <input
                        type="checkbox"
                        checked={pageAllSelected}
                        ref={(el) => { if (el) el.indeterminate = !pageAllSelected && pageIds.some((id) => selected.has(id)); }}
                        onChange={togglePage}
                        aria-label="Select all on this page"
                      />
                    </th>
                  )}
                  {cols.map((c) =>
                    SORT_PICK[c.key] ? (
                      <SortHeader
                        key={c.key}
                        label={c.label}
                        sortKey={c.key}
                        sort={sort}
                        className={c.num ? 'num' : undefined}
                      />
                    ) : (
                      <th key={c.key} className={c.num ? 'num' : undefined}>
                        {c.label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {/* A typo would otherwise leave bare table headers over nothing, which
                    reads as broken rather than as "no match". */}
                {/* "no rows at all" and "no rows match" are different problems and
                    need different offers — the second one is recoverable in a click. */}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={cols.length + (canAssign ? 1 : 0)} style={{ padding: '24px 8px' }}>
                      {anyFilter ? (
                        <span className="muted">
                          No repo matches these filters.{' '}
                          <button className="mini-btn" onClick={clearFilters}>
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        <span className="muted">
                          No repos yet — they appear once <code>gitlab-scan</code> has run.
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                {pg.pageItems.map((r) => (
                  <tr key={r.id} className={r.archived ? 'row-archived' : undefined} style={r.archived ? { opacity: 0.6 } : undefined}>
                    {canAssign && (
                      <td style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          checked={allMatching || selected.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                          aria-label={`Select ${r.pathWithNamespace}`}
                        />
                      </td>
                    )}
                    {cols.map((c) => (
                      <td key={c.key} className={c.num ? 'num' : undefined}>
                        {c.key === 'productLine' && canClassify ? (
                          <span className="pl-cell">
                            <ProductLineCombo
                              value={r.productLine && r.productLine !== 'unclassified' ? r.productLine : ''}
                              options={productLines}
                              onChange={(v) => classify(r.gitlabProjectId, v)}
                            />
                            <ReviewBadge r={r} />
                          </span>
                        ) : (
                          c.render(r)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total} start={pg.start} pageSize={pg.pageSize} onPage={pg.setPage} />
        </div>
      )}

      {/* Confirm-count modal — states the count + the filter scope + the replace
          semantic before applying. This is the safety gate (there is no undo; the
          per-repo audit row captures the prior owner as the manual revert path). */}
      {confirm && (
        <div className="modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => setConfirm(null)}>
          <div className="card" style={{ maxWidth: 460, padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <h3 className="section" style={{ marginTop: 0 }}>Confirm bulk assign</h3>
            <p>
              Assign <strong>{confirm.email}</strong> as <strong>{confirm.role}</strong> to{' '}
              <strong>{selectionCount} repo{selectionCount === 1 ? '' : 's'}</strong>
              <span className="cell-meta"> matching {filterNote}</span>.
            </p>
            <p className="cell-meta">This replaces the current {confirm.role} on any of these repos that already have one. There is no undo — the prior owner is recorded in the audit trail.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="mini-btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={bulkState.loading} onClick={() => void runBulk()}>
                {bulkState.loading ? 'Assigning…' : `Assign ${confirm.role} to ${selectionCount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result toast — best-effort report. */}
      {bulkResult && (
        <div className="card" style={{ position: 'fixed', right: 16, bottom: 16, maxWidth: 420, padding: '12px 16px', zIndex: 50, boxShadow: '0 6px 20px rgba(0,0,0,.25)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <strong>{bulkResult.assignedCount} assigned{bulkResult.skipped.length ? ` · ${bulkResult.skipped.length} skipped` : ''}</strong>
            <button className="own-x" onClick={() => setBulkResult(null)} title="dismiss">×</button>
          </div>
          {bulkResult.skipped.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary className="cell-meta">Show skipped</summary>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {bulkResult.skipped.slice(0, 20).map((s) => (
                  <li key={s.subjectId} className="cell-meta">#{s.subjectId}: {s.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
